import i18n from "@/i18n";
import { isDefined } from "@/utils/Helpers";
import moment from "moment";

export const DEFAULT_INPUT_VARIABLE_INDICATOR = {
  pre: "{",
  post: "}",
};
export type BFInputVariableIndicator = {
  pre: string;
  post: string;
};
export type BFInputVariableMapEntry = {
  key: string;
  value: string;
};

export const generateDateVariables = (
  date?: Date,
  conf?: {
    prefix?: string;
    types?: ("year" | "month" | "day" | "quarter")[];
  }
) => {
  const yearKeys = ["YYYY", "YY"];
  const monthKeys = ["MM-YYYY", "MM/YYYY", "MM-YY", "MMM", "MM", "MMMM"];
  const dayKeys = ["DD-MM-YYYY", "DD/MM/YYYY", "DD-MM-YY", "DD-MM"];
  const quarterYears = ["QQ", "QQ/YYYY", "Q/YYYY", "Q-YYYY"];

  const types = conf.types || ["year", "month", "day"];
  const keys = types.reduce((acc, type) => {
    switch (type) {
      case "year":
        return acc.concat(yearKeys);
      case "month":
        return acc.concat(monthKeys);
      case "day":
        return acc.concat(dayKeys);
      case "quarter":
        return acc.concat(quarterYears);
      default:
        return acc;
    }
  }, []);

  return keys.map((key) => {
    return {
      key: (conf.prefix || "") + key,
      value: isDefined(date) ? moment(date).format(key) : null,
    } as BFInputVariableMapEntry;
  });
};

export const generateDefaultDateVariables = (
  date: Date,
  conf: {
    prefix?: string;
    includeCurrentDate?: boolean;
    includeCurrentMonth?: boolean;
    includeCurrentQuarter?: boolean;
    includeCurrentYear?: boolean;
    includeFollowMonth?: boolean;
    includeFollowQuarter?: boolean;
    includeFollowYear?: boolean;
    includePreviousMonth?: boolean;
    includePreviousPreviousMonth?: boolean;
  }
) => {
  const nextMonth = moment(date).add(1, "month").toDate();
  const nextQuarter = moment(date).add(1, "quarter").toDate();
  const nextYear = moment(date).add(1, "year").toDate();
  const previousMonth = moment(date).subtract(1, "month").toDate();
  const previousPreviousMonth = moment(date).subtract(2, "month").toDate();

  const prefix = conf.prefix || "";
  const keys = [
    ...(conf.includeCurrentDate
      ? generateDateVariables(date, {
          types: ["day"],
          prefix: `${prefix ? prefix + " " : ""}${i18n.t(
            "BFInputVariables.current",
            "Aktuell"
          )} `,
        })
      : []),
    ...(conf.includeCurrentMonth
      ? generateDateVariables(date, {
          types: ["month"],
          prefix: `${prefix ? prefix + " " : ""}${i18n.t(
            "BFInputVariables.current",
            "Aktuell"
          )} `,
        })
      : []),
    ...(conf.includeCurrentQuarter
      ? generateDateVariables(date, {
          types: ["quarter"],
          prefix: `${prefix ? prefix + " " : ""}${i18n.t(
            "BFInputVariables.current",
            "Aktuell"
          )} `,
        })
      : []),
    ...(conf.includeCurrentYear
      ? generateDateVariables(date, {
          types: ["year"],
          prefix: `${prefix ? prefix + " " : ""}${i18n.t(
            "BFInputVariables.current",
            "Aktuell"
          )} `,
        })
      : []),
    ...(conf.includeFollowMonth
      ? generateDateVariables(nextMonth, {
          types: ["month"],
          prefix: `${prefix ? prefix + " " : ""}${i18n.t(
            "BFInputVariables.nextMonth",
            "Folgemonat"
          )} `,
        })
      : []),
    ...(conf.includeFollowQuarter
      ? generateDateVariables(nextQuarter, {
          types: ["month"],
          prefix: `${prefix ? prefix + " " : ""}${i18n.t(
            "BFInputVariables.nextQuarter",
            "Folge Quartal"
          )} `,
        })
      : []),
    ...(conf.includeFollowYear
      ? generateDateVariables(nextYear, {
          types: ["month"],
          prefix: `${prefix ? prefix + " " : ""}${i18n.t(
            "BFInputVariables.nextYear",
            "Folgejahr"
          )} `,
        })
      : []),
    ...(conf.includePreviousMonth
      ? generateDateVariables(previousMonth, {
          types: ["month"],
          prefix: `${prefix ? prefix + " " : ""}${i18n.t(
            "BFInputVariables.previousMonth",
            "Vormonat"
          )} `,
        })
      : []),
    ...(conf.includePreviousPreviousMonth
      ? generateDateVariables(previousPreviousMonth, {
          types: ["month"],
          prefix: `${prefix ? prefix + " " : ""}${i18n.t(
            "BFInputVariables.previousPreviousMonth",
            "Vorvormonat"
          )} `,
        })
      : []),
  ];

  return keys;
};

export const evaluateBFInputVariables = (
  text: string,
  variables: BFInputVariableMapEntry[],
  config?: {
    indicator: BFInputVariableIndicator;
  }
) => {
  const indicator = config?.indicator || DEFAULT_INPUT_VARIABLE_INDICATOR;
  text = variables.reduce((acc, variable) => {
    return acc.replaceAll(
      `${indicator.pre}${variable.key}${indicator.post}`,
      variable.value
    );
  }, text);
  return text;
};
